import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  PiPlusLight,
  PiPencilSimpleLineLight,
  PiTrashLight,
  PiCurrencyDollarLight,
  PiUsersLight,
  PiUserLight,
} from 'react-icons/pi';
import { RootState, useAppDispatch } from '../../../store';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import {
  fetchRateOverrides,
  createRateOverride,
  updateRateOverride,
  deleteRateOverride,
} from '../../../store/actions/economicsActions';
import { formatCurrency } from '../../../utils/formatCurrency';
import { DropdownOption } from '../../shared/TailwindComponents/Dropdown';
import Button from '../../shared/TailwindComponents/Button';
import Table from '../../shared/TailwindComponents/Table';
import GlassCard from '../../shared/brand/GlassCard';
import { KpiTile } from '../../shared/brand/Field';
import UserCell from './UserCell';
import { useMatterCollaborators } from '../../../hooks/useMatterCollaborators';
import type { MatterData } from '../MatterDetails';
import type { RateOverride } from '../../../types/economics';
import type { RateOverridePayload } from '../modals/EditRateOverrideModal';

interface MatterBillingRatesTabProps {
  matterData: MatterData | null;
  userAccessLevel: 'edit' | 'admin' | 'view';
}

/**
 * "Billing rates" tab for a matter (US #2236, task #2257). Lists per-collaborator
 * rate overrides with add / edit / delete. Mutations are limited to the matter
 * owner (admin access) or a firm admin; everyone with access can view.
 */
const MatterBillingRatesTab: React.FC<MatterBillingRatesTabProps> = ({ matterData, userAccessLevel }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  const matterUuid = matterData?.uuid || '';
  const preferredCurrency =
    matterData?.currency || userInfo?.preferences?.general_settings?.currency || 'EUR';
  const canManage = userAccessLevel === 'admin' || !!userInfo?.is_org_admin;

  const [overrides, setOverrides] = useState<RateOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Matter people (collaborators + owner) via the shared hook (FIX 2).
  const collaborators = useMatterCollaborators(matterData);
  const { collaboratorName, collaboratorOptions } = useMemo(() => {
    // Picker options bind to user_uuid; the name map is keyed by uuid AND numeric
    // id so override rows (which carry the numeric user_id) still resolve a label.
    const nameById = new Map<string, string>();
    const options: DropdownOption[] = collaborators.map((c) => {
      const label = c.isOwner ? `${c.name} (${t('Owner')})` : c.name;
      if (c.user_uuid) nameById.set(c.user_uuid, label);
      if (c.user_id) nameById.set(String(c.user_id), label);
      // avatarUrl + email make the Dropdown render the same avatar + name as the table.
      return { value: c.user_uuid, label, avatarUrl: c.avatar, email: c.email };
    });
    return {
      collaboratorName: (id: number | string) => nameById.get(String(id)) || `User ${id}`,
      collaboratorOptions: options,
    };
  }, [collaborators, t]);

  const loadOverrides = useCallback(async () => {
    if (!matterUuid) return;
    setLoading(true);
    setError(false);
    try {
      const data = await dispatch(fetchRateOverrides(matterUuid));
      setOverrides(Array.isArray(data) ? data : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [dispatch, matterUuid]);

  useEffect(() => {
    loadOverrides();
  }, [loadOverrides]);

  const openEditor = (rateOverride: RateOverride | null) => {
    dispatch(
      showModal({
        showModal: true,
        type: 'editRateOverride',
        message: '',
        title: rateOverride ? t('Edit rate override') : t('Add rate override'),
        rateOverride,
        // When editing, the collaborator is fixed; resolve their uuid from the
        // override's numeric user_id and keep that as the sole (locked) option.
        collaborators: rateOverride
          ? collaboratorOptions.filter((o) => {
              const match = collaborators.find((c) => String(c.user_id) === String(rateOverride.user_id));
              return match ? o.value === match.user_uuid : false;
            })
          : collaboratorOptions,
        defaultCurrency: preferredCurrency,
        onSave: async (payload: RateOverridePayload) => {
          if (rateOverride) {
            await dispatch(
              updateRateOverride(matterUuid, rateOverride.id, {
                rate: payload.rate,
                currency: payload.currency,
                notes: payload.notes,
              }),
            );
          } else {
            await dispatch(createRateOverride(matterUuid, payload));
          }
        },
        onAfterSave: loadOverrides,
      }),
    );
  };

  const confirmDelete = (rateOverride: RateOverride) => {
    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        message: t('Remove rate override'),
        subMsg: t('This collaborator will fall back to their default billing rate on this matter.'),
        icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
        isDest: true,
        onConfirm: async () => {
          dispatch(hideModal());
          try {
            await dispatch(deleteRateOverride(matterUuid, rateOverride.id));
            await loadOverrides();
          } catch {
            /* toast already shown by the thunk */
          }
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  };

  /** Matter people with no override - they bill at their standard rate. */
  const standardRatePeople = useMemo(() => {
    const overridden = new Set(overrides.map((o) => String(o.user_id)));
    return collaborators.filter((c) => !overridden.has(String(c.user_id)));
  }, [collaborators, overrides]);

  const columns = [t('Collaborator'), t('Rate'), t('Notes')];
  const rows = overrides.map((o) => ({
    id: o.id,
    [t('Collaborator')]: o,
    [t('Rate')]: formatCurrency(Number(o.rate), o.currency),
    [t('Notes')]: o.notes || '—',
    _raw: o,
  }));

  /** Hover tooltip for the user cell: email, plus override notes when present. */
  const overrideTooltip = (o: RateOverride): React.ReactNode => (
    <span>
      {o.email || o.name || `User ${o.user_id}`}
      {o.notes ? (
        <>
          <br />
          {t('Override notes')}: {o.notes}
        </>
      ) : null}
    </span>
  );

  const renderCell = (column: string, value: any) => {
    if (column === t('Collaborator')) {
      const o = value as RateOverride;
      return <UserCell user={o} fallbackLabel={collaboratorName(o.user_id)} tooltip={overrideTooltip(o)} />;
    }
    return undefined;
  };

  const actions = canManage
    ? [
        {
          label: t('Edit'),
          icon: <PiPencilSimpleLineLight />,
          onClick: (row: any) => openEditor(row._raw as RateOverride),
        },
        {
          label: t('Delete'),
          icon: <PiTrashLight />,
          isDestructive: true,
          onClick: (row: any) => confirmDelete(row._raw as RateOverride),
        },
      ]
    : undefined;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        {/*
          ONE line (mat-17). The cap was `max-w-xl`, which wrapped the sentence
          in two on every width the tab is actually read at; it now takes the
          width it needs and truncates (with the full text on hover) only if
          the viewport genuinely cannot hold it.
        */}
        <p
          className="text-xs text-fg-muted min-w-0 truncate"
          title={t('Per-collaborator rate overrides for this matter. Collaborators without an override bill at their standard rate.')}
        >
          {t('Per-collaborator rate overrides for this matter. Collaborators without an override bill at their standard rate.')}
        </p>
        {canManage && (
          <Button variant="primary" size="small" icon={<PiPlusLight />} onClick={() => openEditor(null)} disabled={!matterUuid}>
            {t('Add override')}
          </Button>
        )}
      </div>

      {/* The same totals rhythm the other two sub-tabs open with: how many
          people bill at a matter rate, and how many at their own. */}
      {!error && collaborators.length > 0 && (
        <GlassCard padding="none">
          <div className="grid grid-cols-3 divide-x divide-hairline-soft">
            <KpiTile icon={<PiUsersLight />} label={t('Collaborators')} value={collaborators.length} />
            <KpiTile icon={<PiCurrencyDollarLight />} label={t('Rate overrides')} value={overrides.length} />
            <KpiTile
              icon={<PiUserLight />}
              label={t('On standard rate')}
              value={standardRatePeople.length}
            />
          </div>
        </GlassCard>
      )}

      {error ? (
        <div className="text-sm text-fg-muted py-8 text-center">
          {t('Could not load billing rates.')}{' '}
          <button className="text-primary-600 underline" onClick={loadOverrides}>
            {t('Retry')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
        <GlassCard padding="none" icon={<PiCurrencyDollarLight />} title={t('Rate overrides')}>
        <Table
            isLoading={loading && overrides.length === 0}
          columns={columns}
          data={rows}
          actions={actions}
          renderCell={renderCell}
          min="min-h-[6rem]"
          max="max-h-[calc(100vh-280px)]"
          isLoadingMore={loading}
          emptyState={{
            icon: <PiCurrencyDollarLight className="h-6 w-6" />,
            headline: t('No rate overrides'),
            subtext: canManage
              ? t('Add an override to bill a collaborator at a matter-specific rate.')
              : t('No collaborator has a matter-specific rate on this matter.'),
            ...(canManage ? { button: { label: t('Add override'), onClick: () => openEditor(null) } } : {}),
          }}
        />
        </GlassCard>

        {/*
          Who is NOT overridden.

          The table lists overrides only, so a collaborator on their standard
          rate simply did not appear and the tab could not answer "what does
          this person bill at on this matter?". Listing them closes that gap
          with data already in hand.

          FLAGGED: the standard AMOUNT is not shown, because it is not in this
          component's data - it lives behind `/organizations/me/users-billing-rates`
          and fetching it would be a data-layer change.
        */}
        {standardRatePeople.length > 0 && (
          <div>
            <GlassCard padding="none" icon={<PiUserLight />} title={t('On their standard rate')}>
              <div className="divide-y divide-hairline-soft">
                {standardRatePeople.map((person) => (
                  <div
                    key={person.user_uuid || person.user_id}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <UserCell
                      user={{
                        name: person.name,
                        email: person.email,
                        avatar_url: person.avatar,
                        user_id: person.user_id,
                      }}
                    />
                    <span className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-fg-muted">{t('Standard rate')}</span>
                      {canManage && (
                        <Button variant="outline" size="small" onClick={() => openEditor(null)}>
                          {t('Override')}
                        </Button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </GlassCard>
          </div>
        )}
        </div>
      )}
    </div>
  );
};

export default MatterBillingRatesTab;
